import _ from "lodash";
import { action } from "mobx";
import { observer } from "mobx-react";
import { Button } from "primereact/button";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import React, { useContext, useMemo } from "react";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { candidateAPI } from "../../../../api/candidate";
import { useCustomAlignDropdownOverlay } from "../../../../Components/useCustomAlignOverlay";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { invalidateQueries } from "../../../../WSUtils/useLatestQuery";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { concatClassnames } from "../../../../WSUtils/util";
import { CVEditContext } from "../../CVEditContext";
import { CVEditListStyles } from "../../CVEditListStyles";
import { CVEditLanguage } from "./CVEditLanguage";
import { CVEditLanguageSegmentStore } from "./CVEditLanguageSegmentStore";

export interface CVEditLanguageListProps {
  onChange: (newValue: CVEditLanguage[]) => any;
  languageSegmentStore: CVEditLanguageSegmentStore;
}

export const CVEditLanguageList = observer(
  ({ onChange, languageSegmentStore }: CVEditLanguageListProps) => {
    const L = useLocStore(LocContext.CV);
    const cvEditContext = useContext(CVEditContext);

    const items = languageSegmentStore.languageField.store.value;

    const languageOptions = useMemo(
      () => [
        {
          label: L.id("a26eab912c6c").loc("Grunnleggende"),
          value: 1,
        },
        {
          label: L.id("d6039758ab6b").loc("Bra"),
          value: 2,
        },
        {
          label: L.id("bab031273cbb").loc("Flytende"),
          value: 3,
        },
      ],
      []
    );

    if (languageSegmentStore.showPlaceholder) return null;

    return (
      <CVEditLanguageListStyles
        className={concatClassnames([
          "cve-list cve-language-list",
          cvEditContext.layoutClassName,
        ])}
        id={languageSegmentStore.languageListId}
        style={{
          height: languageSegmentStore.languageListForcedHeight,
          overflow: languageSegmentStore.languageListForcedHeight
            ? "hidden"
            : undefined,
        }}
      >
        {_.orderBy(items, ["languageType", "languageTypeId"]).map(
          (language, index) => (
            <div key={language.id} className="cve-language-list-item">
              <div className="cve-language-list-item-name cve-listing-text">
                {language.languageType}
              </div>
              <div className="cve-listing-buttons">
                <Buttons
                  cveLanguage={language}
                  onChange={onChange}
                  languageOptions={languageOptions}
                  languageSegmentStore={languageSegmentStore}
                />
              </div>
            </div>
          )
        )}
      </CVEditLanguageListStyles>
    );
  }
);

interface ButtonsProps {
  cveLanguage: CVEditLanguage;
  onChange: (newValue: CVEditLanguage[]) => any;
  languageOptions: { label: string; value: number }[];
  languageSegmentStore: CVEditLanguageSegmentStore;
}

const Buttons = observer(
  ({
    cveLanguage,
    onChange,
    languageOptions,
    languageSegmentStore,
  }: ButtonsProps) => {
    const L = useLocStore(LocContext.CV);
    const userInfo = useContext(UserInfoContext);
    const queryClient = useQueryClient();

    const store = languageSegmentStore.languageField.store;

    const valueVersioner = store.valueVersioner;

    const onLevelChange: DropdownProps["onChange"] = action((evt) => {
      evt.originalEvent.persist();

      const newValue = store.value.map((v) => {
        if (v.id === cveLanguage.id) {
          return {
            ...v,
            level: evt.value,
          };
        }
        return v;
      });

      const mutate = () =>
        candidateAPI.editKnowledgeForCandidate({
          showSentryDialogOnError: true,
          userInfo: userInfo,
          firmId: userInfo.idFirm,
          candidateId: userInfo.idCandidate,
          knowledgeId: cveLanguage.id,
          knowledgeType: cveLanguage.languageTypeId,
          comment: "",
          level: evt.value,
          expirationDate: null,
          duplicateCheck: false,
        });

      const invalidate = () =>
        invalidateQueries(queryClient, [
          "candidateAPI.getLanguageForCandidate",
        ]);

      return valueVersioner.onChange({
        newValue,
        mutateBackend: mutate,
        invalidateQuery: invalidate,
      });
    });

    const ref = useCustomAlignDropdownOverlay();

    return (
      <>
        <Dropdown
          ref={ref}
          options={languageOptions}
          value={cveLanguage.level}
          onChange={onLevelChange}
          disabled={cveLanguage.id < 0}
          className="cve-language-list-level-dropdown"
        />

        <Button
          icon="pi pi-trash"
          disabled={cveLanguage.id < 0}
          title={L.id("07f424b02f46").loc("Fjern språk")}
          className="p-button-rounded p-button-text p-button-icon-only cve-language-list-trashcan"
          onClick={action((evt) => {
            evt.preventDefault();

            const newValue =
              languageSegmentStore.languageField.store.value.filter(
                (_cveLanguage) => _cveLanguage.id !== cveLanguage.id
              );

            return onChange(newValue);
          })}
        />
      </>
    );
  }
);

export const CVEditLanguageListStyles = styled(CVEditListStyles)`
  &.cve-language-list {
    &.cve-m,
    &.cve-s {
      .cve-language-list-item {
        margin-left: -0.75rem;
        padding-left: 0.75rem;

        max-width: calc(100% + 0.75rem);
      }
    }

    &.cve-language-list.cve-language-list {
      margin-top: 1rem;
      margin-bottom: 1rem;
    }

    .cve-language-list-level-dropdown {
      border: none;
      background: transparent;
      overflow: hidden;
    }

    .cve-language-list-trashcan {
      flex-shrink: 0;
    }

    .cve-language-listing-position {
      font-weight: bold;
    }

    .cve-language-list-item-name {
      padding: 0.5rem 0;
      text-overflow: ellipsis;
    }

    .cve-language-list-item {
      display: flex;
      justify-content: space-between;
      align-items: center;

      margin-left: -1rem;
      padding-left: 1rem;

      max-width: calc(100% + 1rem);

      .cve-listing-buttons {
        float: none;
        margin-top: 0;

        display: flex;
        flex-wrap: nowrap;
        flex-basis: 0;
        flex-grow: 1;
        min-width: 137px;
        justify-content: flex-end;
      }

      &:nth-child(even) {
        background: var(--surface-50);
      }
    }
  }
`;
